import { api, ErroreApi } from './api.ts'
import type { AllegatoDaInviare } from './posta.ts'

/**
 * Messaggio e rubrica come in Roundcube (539, richiesta di Davide del 2/11/2026): le chiamate di
 * rotte/posta-messaggio.ts e rotte/posta-rubrica.ts dell'API. Le forme dei messaggi stanno in posta.ts.
 */

// --- conferma di lettura ---------------------------------------------------------------------------------

/** «Invia la conferma» (true) o «Ignora» (false): la scelta vale per il messaggio, anche per i colleghi. */
export const decidiConferma = (postaId: string, invia: boolean) =>
  api.post<{ esito: 'inviata' | 'ignorata' }>(`/posta/messaggi/${postaId}/conferma-lettura`, { invia })

/** Come si legge una conferma ricevuta, dalla disposizione di RFC 8098. */
export function testoConferma(disposizione: string): string {
  switch (disposizione) {
    case 'displayed': return 'Letto da'
    case 'deleted': return 'Cancellato senza leggerlo da'
    case 'denied': return 'Conferma rifiutata da'
    default: return `Conferma (${disposizione}) da`
  }
}

// --- mostra originale ------------------------------------------------------------------------------------

export type Originale = {
  /** Come sono arrivate (`originali`), oppure quelle della ricostruzione. */
  intestazioni: string
  originali: boolean
  /** Sempre ricostruito dalle parti archiviate, senza il contenuto degli allegati; null con il testo in Glacier. */
  sorgente: string | null
  troncato: boolean
  /** Allegati del messaggio, che il sorgente non contiene. */
  allegati: number
}

/** La rotta di «Mostra originale» nella webmail e nell'archivio delle caselle. */
export const percorsoOriginale = (postaId: string) => `/posta/messaggi/${postaId}/originale`
export const percorsoOriginaleArchiviato = (casellaId: string, postaId: string) =>
  `/posta/archivio/${casellaId}/messaggi/${postaId}/originale`

export const leggiOriginale = (percorso: string) => api.get<Originale>(percorso)

// --- inoltra come allegato -------------------------------------------------------------------------------

export type InoltroComeAllegato = {
  allegato: AllegatoDaInviare
  oggetto: string
  /** Allegati del messaggio rimasti fuori dal .eml, con il motivo (antivirus). */
  omessi: { nome: string; motivo: string }[]
}

export const preparaInoltroComeAllegato = (postaId: string) =>
  api.post<InoltroComeAllegato>(`/posta/messaggi/${postaId}/come-allegato`)

/** «Inoltra» con gli allegati del messaggio (539): copiati come allegati del compositore, tranne i non puliti. */
export const preparaAllegatiPerInoltro = (postaId: string) =>
  api.post<{ allegati: AllegatoDaInviare[]; omessi: { nome: string; motivo: string }[] }>(
    `/posta/messaggi/${postaId}/allegati-per-inoltro`)

// --- gruppi della rubrica --------------------------------------------------------------------------------

export type IndirizzoPerGruppo = { indirizzo: string; nome?: string | null }

export const creaGruppo = (casellaId: string, nome: string, contatti: string[], indirizzi: IndirizzoPerGruppo[] = []) =>
  api.post<{ id: string }>(`/posta/caselle/${casellaId}/rubrica/gruppi`, { nome, contatti, indirizzi })

export const modificaGruppo = (id: string, cambio: {
  nome?: string; aggiungi?: string[]; togli?: string[]; indirizzi?: IndirizzoPerGruppo[]
}) => api.patch<{ id: string; aggiunti: number; tolti: number }>(`/posta/rubrica/gruppi/${id}`, cambio)

export const eliminaGruppo = (id: string) => api.elimina<{ eliminato: boolean }>(`/posta/rubrica/gruppi/${id}`)

// --- importazione ed esportazione ------------------------------------------------------------------------

export type StatoImportazione = 'nuovo' | 'presente' | 'ripetuto' | 'casella' | 'non_valido'

export type AnteprimaImportazione = {
  voci: { indirizzo: string; nome: string | null; gruppi: string[]; stato: StatoImportazione }[]
  riepilogo: { nuovi: number; presenti: number; ripetuti: number; non_validi: number; casella: number }
}

export type VoceDaImportare = { indirizzo: string; nome: string | null; gruppi: string[] }

/** Al massimo tante voci per volta: un file piu' grande si importa in piu' passi. */
export const MASSIMO_VOCI = 5000

export const anteprimaImportazione = (casellaId: string, voci: VoceDaImportare[]) =>
  api.post<AnteprimaImportazione>(`/posta/caselle/${casellaId}/rubrica/importa`, { voci, anteprima: true })

export const importa = (casellaId: string, voci: VoceDaImportare[], gruppi: boolean) =>
  api.post<{ importati: number; gruppi_creati: number }>(`/posta/caselle/${casellaId}/rubrica/importa`,
    { voci, anteprima: false, gruppi })

/**
 * Il testo di un file scelto: UTF-8, oppure Windows-1252 se non lo e' (Outlook su Windows esporta cosi' le
 * lettere accentate). Il file non lascia il computer.
 */
export async function testoDelFile(file: File): Promise<string> {
  const byte = new Uint8Array(await file.arrayBuffer())
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(byte)
  } catch {
    return new TextDecoder('windows-1252').decode(byte)
  }
}

/**
 * Esporta la rubrica e scarica il file. E' una POST: l'API scrive l'evento nel registro, e impersonando non
 * si esporta (524).
 */
export async function esportaRubrica(casellaId: string, formato: 'vcf' | 'csv'): Promise<void> {
  const risposta = await fetch(`/api/posta/caselle/${casellaId}/rubrica/esporta`, {
    method: 'POST', credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ formato }),
  })
  if (!risposta.ok) {
    const corpo = await risposta.json().catch(() => null) as { errore?: string; codice?: string } | null
    throw new ErroreApi(corpo?.errore ?? `Il server non ha risposto come doveva (errore ${risposta.status}).`,
      risposta.status, corpo)
  }
  const disposizione = risposta.headers.get('content-disposition') ?? ''
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposizione)
  const nome = utf8 ? decodeURIComponent(utf8[1]!) : `rubrica.${formato}`
  const indirizzo = URL.createObjectURL(await risposta.blob())
  const a = document.createElement('a')
  a.href = indirizzo
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(indirizzo), 1000)
}
